import { ReactElement } from 'react'

import { clsx } from 'clsx'

import { Image as TImage } from '@tribeplatform/gql-client/types'
import { Image as ImageComponent } from '@tribeplatform/react-components/common/components'
import { useI18n } from '@tribeplatform/react-components/i18n'
import { handleUploadFileError } from '@tribeplatform/react-components/utils/error'
import { useMedia, useImageUploader } from '@tribeplatform/react-sdk/hooks'
import { FileDropZone } from '@tribeplatform/react-ui-kit/FileUpload'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'

export interface LogoPickerProps {
  label: string
  helperText: ReactElement
  imageId: string
  onChange: (imageId: string) => void
  darkMode?: boolean
}

export const LogoPicker = ({
  label,
  helperText,
  imageId,
  onChange,
}: LogoPickerProps) => {
  const intl = useI18n()
  const { $t } = intl
  const { uploadImage, isLoading: isUploadingImages } = useImageUploader()

  const { data: media, isInitialLoading: isMediaLoading } = useMedia({
    variables: {
      id: imageId,
    },
    fields: 'all',
    useQueryOptions: {
      enabled: !!imageId,
    },
  })

  const image = media as TImage

  const onChangeFile = async (files: File[]) => {
    const file = files?.[0]

    if (!file) {
      return
    }

    const image = new Image()
    image.src = URL.createObjectURL(file)
    image.onload = async () => {
      const { width, height } = image

      try {
        const uploadedImage = await uploadImage(file, {
          width,
          height,
        })
        onChange(uploadedImage?.id)
      } catch (error) {
        handleUploadFileError(intl)(error)
      }
    }
  }

  return (
    <FormControl.Layout name="logo" label={label} helperText={helperText}>
      <FileDropZone
        type="image"
        accept="image/png, image/jpeg"
        allowMultiple={false}
        onDrop={onChangeFile}
        loading={isUploadingImages}
        className="p-3"
      >
        <div className="h-32">
          {image && (
            <ImageComponent
              className={clsx(
                'block mx-auto object-contain h-32 bg-checkerboard',
              )}
              src={image.urls?.small ?? image.url}
              alt={label}
            />
          )}
          {!image && !isMediaLoading && (
            <FileDropZone.Label
              actionTitle={$t({
                defaultMessage:
                  'Click to upload a file, or drag and drop it here.',
                id: 'Form.File.UploadAFile',
              })}
            />
          )}
        </div>
      </FileDropZone>
    </FormControl.Layout>
  )
}
